import { memo, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { BottomSheet } from '@turnifly/shared/ui'
import { useAuthStore } from '@turnifly/shared/stores'
import { useUpdateLifeSettings } from '@turnifly/shared/hooks'

interface LifeSettingsSheetProps {
  isOpen: boolean
  onClose: () => void
}

export const LifeSettingsSheet = memo(function LifeSettingsSheet({
  isOpen,
  onClose,
}: LifeSettingsSheetProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { mutate: save, isPending } = useUpdateLifeSettings()

  const [birthDate, setBirthDate] = useState(user?.birth_date ?? '')
  const [lifeExpectancy, setLifeExpectancy] = useState(user?.life_expectancy ?? 80)

  // Sync when sheet opens
  useEffect(() => {
    if (isOpen) {
      setBirthDate(user?.birth_date ?? '')
      setLifeExpectancy(user?.life_expectancy ?? 80)
    }
  }, [isOpen, user?.birth_date, user?.life_expectancy])

  const today = new Date().toISOString().split('T')[0]

  const handleSave = () => {
    save(
      { birth_date: birthDate, life_expectancy: lifeExpectancy },
      { onSuccess: onClose },
    )
  }

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={t('life.settings')}>
      <div className="px-5 pb-6 space-y-5">
        {/* Birth date */}
        <div>
          <label className="text-[11px] font-semibold text-hint uppercase tracking-wider">
            {t('life.birthDate')}
          </label>
          <input
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            min="1930-01-01"
            max={today}
            className="w-full mt-1.5 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-text text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary/50 outline-none transition-all"
          />
        </div>

        {/* Life expectancy */}
        <div>
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-semibold text-hint uppercase tracking-wider">
              {t('life.lifeExpectancy')}
            </label>
            <span className="text-sm font-display font-bold text-primary">{lifeExpectancy}</span>
          </div>
          <input
            type="range"
            min={50}
            max={120}
            value={lifeExpectancy}
            onChange={(e) => setLifeExpectancy(Number(e.target.value))}
            className="w-full mt-2 accent-primary"
          />
        </div>

        {/* Save */}
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={handleSave}
          disabled={!birthDate || isPending}
          className="w-full py-3.5 rounded-2xl bg-gradient-to-tr from-primary to-accent text-white font-semibold text-sm shadow-neon disabled:opacity-50 tap-highlight-transparent"
        >
          {isPending ? t('common.saving') : t('common.save')}
        </motion.button>
      </div>
    </BottomSheet>
  )
})
